/**
 * Entry points for the safety UI. Anything can call these (an avatar click in
 * the 3D scene, the chat "…" menu, the online-players list); the 2D overlay
 * mounts itself into its own React root on first use, so pages don't have to
 * render anything. In VR (`vr: true`) the in-scene panel opens instead.
 */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { useSafetyUi, type SafetyView } from '../../stores/safetyUiStore';
import { isSelf, type SafetyTarget } from '../../services/safety';
import { getPresenceKey } from '../../services/realtimeManager';
import { useWorldStore } from '../../stores/worldStore';

let root: Root | null = null;

async function ensureOverlay() {
  if (root || typeof document === 'undefined') return;
  let host = document.getElementById('gx-safety-root');
  if (!host) {
    host = document.createElement('div');
    host.id = 'gx-safety-root';
    document.body.appendChild(host);
  }
  const { default: SafetyOverlay } = await import('./SafetyOverlay');
  if (root) return;
  root = createRoot(host);
  root.render(React.createElement(SafetyOverlay));
}

// Fetch the overlay's code once the page is idle, so the first card opens instantly.
if (typeof window !== 'undefined') {
  const idle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 3000));
  idle(() => { void import('./SafetyOverlay'); });
}

// A toast (e.g. "Muted Alex" from the chat menu) needs the overlay mounted.
useSafetyUi.subscribe((s, prev) => { if (s.toast && s.toast !== prev.toast) void ensureOverlay(); });

function open(view: SafetyView, vr?: boolean) {
  if (vr) {
    useSafetyUi.setState({ vrView: view, vrOpenedAt: Date.now() });
    return;
  }
  void ensureOverlay();
  useSafetyUi.setState({ view });
  // Desktop first-person mode grabs the pointer on any canvas click; the card
  // needs a cursor. (SafetyOverlay keeps releasing it while it's open.)
  try { if (document.pointerLockElement) document.exitPointerLock(); } catch { /* unsupported */ }
}

/** The small player card: name + Mute / Block / Report. Ignores yourself. */
export function openPlayerCard(target: SafetyTarget, opts: { vr?: boolean } = {}) {
  if (isSelf(target, getPresenceKey())) return;
  open({ kind: 'card', target }, opts.vr);
}

export function openReportDialog(target: SafetyTarget, opts: { vr?: boolean } = {}) {
  if (isSelf(target, getPresenceKey())) return;
  open({ kind: 'report', target }, opts.vr);
}

/** Everyone in the room, each with the same actions. */
export function openPeopleList(opts: { vr?: boolean } = {}) {
  open({ kind: 'people' }, opts.vr);
}

export function closeSafety(opts: { vr?: boolean } = {}) {
  if (opts.vr) useSafetyUi.setState({ vrView: null });
  else useSafetyUi.setState({ view: null });
}

/** Other people currently in the room (not me, not my other tabs' self entry). */
export function roomPeople(): SafetyTarget[] {
  const myKey = getPresenceKey();
  const me = useWorldStore.getState().user?.id || null;
  const seen = new Set<string>();
  const out: SafetyTarget[] = [];
  for (const p of useWorldStore.getState().onlinePlayers) {
    if (p.id === myKey) continue;
    if (me && p.userId === me) continue; // my own other tabs
    const dedupe = p.userId || `k:${p.id}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({ userId: p.userId || null, key: p.id, name: p.userName || 'Player' });
  }
  return out;
}

/** 'mobile' on touch / narrow screens, else 'desktop' (VR passes its own). */
export function currentPlatform(): 'desktop' | 'mobile' {
  try {
    if (window.matchMedia?.('(pointer: coarse)').matches || window.matchMedia?.('(max-width: 768px)').matches) return 'mobile';
  } catch { /* old browser */ }
  return 'desktop';
}
